import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import { ArrowLeft, ArrowRight, Mail, Check } from 'lucide-react';
import { QUESTIONS, calculateResult, getStatedIntent } from '../../lib/attraction-alignment-data';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] },
});

// --- LANDING ---
const Landing = ({ onStart }) => (
  <section className="min-h-[80vh] px-6 pt-28 sm:pt-32 pb-16">
    <div className="max-w-lg mx-auto text-center">
      <motion.span
        {...fade(0)}
        className="inline-block text-xs font-medium tracking-[0.2em] uppercase text-[hsl(var(--teal))] mb-6"
      >
        Guided Self-Discovery
      </motion.span>
      <motion.h1
        {...fade(0.06)}
        className="text-3xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight leading-[1.15] mb-5"
      >
        Is your type{' '}
        <span className="italic text-[hsl(var(--gold))]">actually</span> your type?
      </motion.h1>
      <motion.p
        {...fade(0.12)}
        className="text-base sm:text-lg text-muted-foreground font-light max-w-md mx-auto mb-3"
      >
        What grabs your attention and what actually fits aren't always the same thing.
      </motion.p>
      <motion.p
        {...fade(0.16)}
        className="text-base sm:text-lg text-muted-foreground font-light max-w-md mx-auto mb-8"
      >
        See whether what you're drawn to actually lines up with what you say you want.
      </motion.p>
      <motion.div {...fade(0.22)}>
        <button
          onClick={onStart}
          className="rounded-full px-8 py-6 text-base font-medium bg-[hsl(var(--gold))] text-white hover:bg-[hsl(var(--gold)/0.9)] transition-transform hover:scale-[0.98] active:scale-[0.96]"
          data-testid="attraction-start-btn"
        >
          Start Chemistry Check
        </button>
        <p className="text-xs text-muted-foreground/60 mt-4">Takes about 3–5 minutes</p>
      </motion.div>
    </div>
  </section>
);

// --- QUESTION ---
const Question = ({ question, questionIndex, total, answer, onAnswer, onNext, onBack }) => {
  const progress = ((questionIndex + 1) / total) * 100;
  const canNext = answer !== null && answer !== undefined;

  return (
    <section className="min-h-[80vh] px-6 pt-28 sm:pt-32 pb-16">
      <div className="max-w-lg mx-auto w-full">
        <motion.div {...fade(0)} className="mb-10">
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>Question {questionIndex + 1} of {total}</span>
          </div>
          <div className="h-1 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[hsl(var(--gold))] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </motion.div>

        <motion.h2
          key={question.id + '-prompt'}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-xl sm:text-2xl font-light text-foreground leading-snug mb-8"
        >
          {question.prompt}
        </motion.h2>

        <AnimatePresence mode="wait">
          <motion.div
            key={question.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {question.type === 'choice' ? (
              <div className="space-y-3">
                {question.options.map((option, i) => {
                  const isSelected = answer === i;
                  return (
                    <Card
                      key={i}
                      onClick={() => onAnswer(i)}
                      className={`cursor-pointer transition-all border ${
                        isSelected
                          ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal)/0.05)]'
                          : 'border-border hover:border-[hsl(var(--teal)/0.3)]'
                      }`}
                      data-testid={`attraction-option-${i}`}
                    >
                      <CardContent className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
                              isSelected ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal))]' : 'border-muted-foreground/30'
                            }`}
                          >
                            {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <span className="text-sm text-foreground leading-relaxed">{option.label}</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="pt-4 pb-2">
                <div className="flex justify-between text-xs text-muted-foreground mb-6">
                  <span>{question.labels[0]}</span>
                  <span>{question.labels[1]}</span>
                </div>
                <div className="flex justify-between gap-2">
                  {Array.from({ length: question.max - question.min + 1 }, (_, i) => {
                    const val = question.min + i;
                    const isSelected = answer === val;
                    return (
                      <button
                        key={val}
                        onClick={() => onAnswer(val)}
                        className={`flex-1 h-12 rounded-lg text-sm font-medium transition-all ${
                          isSelected ? 'bg-[hsl(var(--teal))] text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                        data-testid={`attraction-slider-${val}`}
                      >
                        {val}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between items-center mt-10">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="attraction-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            onClick={onNext}
            disabled={!canNext}
            className={`flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-all ${
              canNext ? 'bg-[hsl(var(--gold))] text-white hover:bg-[hsl(var(--gold)/0.9)]' : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
            data-testid="attraction-next-btn"
          >
            {questionIndex === total - 1 ? 'See Results' : 'Next'} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

// --- RESULTS ---
const Results = ({ result, archetypeKey, answers, scores, statedIntent, onTakeSurvey, onKeepExploring }) => {
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || sending) return;
    setSending(true);
    try {
      await fetch(`${API_URL}/api/emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'attraction-alignment' }),
      });
      setEmailSent(true);
    } catch {
      // silent
    }
    setSending(false);
  };

  useEffect(() => {
    const storeResult = async () => {
      try {
        await fetch(`${API_URL}/api/attraction-alignment-results`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            answers,
            scores,
            archetype: archetypeKey,
            stated_desired_connection_type: statedIntent,
          }),
        });
      } catch {
        // silent
      }
    };
    storeResult();
  }, [answers, scores, archetypeKey, statedIntent]);

  return (
    <section className="min-h-[80vh] px-6 pt-28 sm:pt-32 pb-20">
      <div className="max-w-lg mx-auto w-full">
        <motion.span
          {...fade(0)}
          className="inline-block text-xs font-medium tracking-[0.2em] uppercase text-[hsl(var(--teal))] mb-4"
        >
          What We're Seeing
        </motion.span>

        <motion.h1
          {...fade(0.06)}
          className="text-3xl sm:text-4xl font-light text-foreground tracking-tight mb-6"
          data-testid="attraction-result-label"
        >
          {result.label}
        </motion.h1>

        <motion.p
          {...fade(0.12)}
          className="text-base text-muted-foreground leading-relaxed mb-8"
          data-testid="attraction-result-narrative"
        >
          {result.narrative}
        </motion.p>

        {statedIntent && (
          <motion.div
            {...fade(0.16)}
            className="mb-8 rounded-2xl border border-border/60 bg-[hsl(var(--peach)/0.5)] px-5 py-4"
            data-testid="attraction-stated-intent"
          >
            <p className="text-xs font-medium tracking-[0.18em] uppercase text-[hsl(var(--gold))] mb-1.5">
              You said you want
            </p>
            <p className="text-sm text-foreground/85 leading-relaxed">{statedIntent}</p>
          </motion.div>
        )}

        <motion.div {...fade(0.20)} className="mb-12">
          <div className="flex flex-wrap gap-2">
            {result.bullets.map((bullet, i) => (
              <span
                key={i}
                className="px-3 py-1.5 rounded-full text-xs font-medium bg-[hsl(var(--teal)/0.08)] text-[hsl(var(--teal))]"
                data-testid={`attraction-bullet-${i}`}
              >
                {bullet}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Reflection Bridge */}
        <motion.div
          {...fade(0.24)}
          className="border-t border-border/50 pt-10 mb-10"
          data-testid="attraction-reflection-bridge"
        >
          <span className="inline-block text-xs font-medium tracking-[0.18em] uppercase text-[hsl(var(--gold))] mb-3">
            Before you do anything else
          </span>
          <h3 className="text-lg font-light text-foreground mb-3 leading-snug">
            What we're showing you is a perspective, not a verdict.
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-3">
            If this feels accurate, it may be worth asking whether what you describe to HAEVN
            reflects what actually fits… or simply what has grabbed your attention in the past.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            If that distinction feels blurry, that's okay too. That's exactly the kind of clarity
            this process is designed to help create.
          </p>
        </motion.div>

        {/* CTAs */}
        <motion.div {...fade(0.30)} className="mb-10">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onTakeSurvey}
              className="flex-1 rounded-full px-6 py-4 text-sm font-medium bg-[hsl(var(--gold))] text-white hover:bg-[hsl(var(--gold)/0.9)] transition-all"
              data-testid="attraction-cta-survey"
            >
              Tell HAEVN What You're Looking For
            </button>
            <button
              onClick={onKeepExploring}
              className="flex-1 rounded-full px-6 py-4 text-sm font-medium border border-border text-foreground hover:bg-secondary transition-all"
              data-testid="attraction-cta-explore"
            >
              Keep Exploring
            </button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground/70 italic">
            Takes about 15 minutes. Then HAEVN takes it from there.
          </p>
        </motion.div>

        {/* Email Capture */}
        <motion.div {...fade(0.34)}>
          {emailSent ? (
            <div className="flex items-center gap-2 text-sm text-[hsl(var(--teal))]">
              <Check className="w-4 h-4" />
              <span>We'll send a copy to your inbox.</span>
            </div>
          ) : (
            <form onSubmit={handleEmailSubmit} className="flex gap-3">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Want a copy of your results?"
                  className="w-full pl-10 pr-4 py-3 rounded-full bg-muted border border-border text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-[hsl(var(--teal)/0.5)]"
                  data-testid="attraction-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={!email || sending}
                className="rounded-full px-5 py-3 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-all"
                data-testid="attraction-email-submit"
              >
                Send
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

// --- MAIN ---
export const AttractionAlignment = ({ onBackToHub, onGoToHAEVN }) => {
  const [step, setStep] = useState('landing');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [result, setResult] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, currentIndex]);

  const handleStart = useCallback(() => {
    setStep('quiz');
    setCurrentIndex(0);
    setAnswers(Array(QUESTIONS.length).fill(null));
  }, []);

  const handleAnswer = useCallback(
    (value) => {
      setAnswers((prev) => {
        const updated = [...prev];
        updated[currentIndex] = value;
        return updated;
      });
    },
    [currentIndex],
  );

  const handleNext = useCallback(() => {
    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex((p) => p + 1);
    } else {
      const res = calculateResult(answers);
      setResult(res);
      setStep('results');
    }
  }, [currentIndex, answers]);

  const handleBack = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((p) => p - 1);
    } else {
      setStep('landing');
    }
  }, [currentIndex]);

  if (step === 'landing') {
    return <Landing onStart={handleStart} />;
  }

  if (step === 'quiz') {
    return (
      <Question
        question={QUESTIONS[currentIndex]}
        questionIndex={currentIndex}
        total={QUESTIONS.length}
        answer={answers[currentIndex]}
        onAnswer={handleAnswer}
        onNext={handleNext}
        onBack={handleBack}
      />
    );
  }

  if (step === 'results' && result) {
    return (
      <Results
        result={result.result}
        archetypeKey={result.archetype}
        answers={answers}
        scores={result.scores}
        statedIntent={getStatedIntent(answers)}
        onTakeSurvey={() => onGoToHAEVN && onGoToHAEVN()}
        onKeepExploring={() => onBackToHub && onBackToHub()}
      />
    );
  }

  return null;
};
